import React, { useEffect } from "react";
import { Button, Col, Divider, Drawer, Form, Input, Row, Select, Space, Upload, Typography, message, InputNumber } from "antd";
import { ExpandOutlined, CompressOutlined, ApartmentOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import {
  BUSINESS_LOCATION_STATUSES,
  formatEnumLabel,
} from "../constants";

export default function BusinessLineDrawer({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
  locationTypes = [],
  locationTypesLoading = false,
}) {
  const [form] = Form.useForm();
  const [fullWidth, setFullWidth] = React.useState(false);
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const [profilePhotoFiles, setProfilePhotoFiles] = React.useState([]);
  const [profilePhotoUploading, setProfilePhotoUploading] = React.useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setProfilePhotoFiles([]);
      if (initialValues) {
        form.setFieldsValue({
          ...initialValues,
          contactInformation: initialValues.contactInformation || {},
          locationInformation: initialValues.locationInformation || {},
          businessLocationType:
            initialValues.businessLocationType?.id ||
            initialValues.businessLocationTypeId ||
            initialValues.businessLocationType?.code ||
            initialValues.businessLocationType ||
            null,
        });

        if (initialValues.profilePhoto) {
          setProfilePhotoFiles([
            { uid: String(initialValues.profilePhoto), name: "profile photo", status: "done" },
          ]);
        }
      }
    }
  }, [open, form, initialValues]);

  const uploadToField = async ({ file, onSuccess, onError }, fieldName, fileType) => {
    try {
      setProfilePhotoUploading(true);
      const userId = user?.id;
      if (!userId) throw new Error("Missing user id");
      const res = await uploadFile(userId, file, { purpose: "USER_PROFILE", fileType });
      form.setFieldValue(fieldName, res.fileId);
      setProfilePhotoFiles([{ uid: String(res.fileId), name: file?.name || "profile photo", status: "done" }]);
      onSuccess?.(res, file);
      message.success(`${file?.name || "File"} uploaded`);
      toast.success("File uploaded");
    } catch (err) {
      onError?.(err);
      message.error(err?.message || "Failed to upload file");
      toast.error(err?.message || "Failed to upload file");
    } finally {
      setProfilePhotoUploading(false);
    }
  };

  const handleFinish = async (values) => {
    const payload = {
      ...values,
      profilePhoto: form.getFieldValue("profilePhoto") || null,
      contactInformation: values.contactInformation || null,
      locationInformation: values.locationInformation || null,
    };
    await onSubmit(payload);
  };

  return (
    <Drawer
      title={
        <Space>
          <ApartmentOutlined />
          <span>
            {initialValues?.id ? "Edit Business Location" : "Create Business Location"}
          </span>
        </Space>
      }
      width={fullWidth ? "100vw" : 720}
      styles={{
        body: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        header: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        footer: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
      }}
      open={open}
      onClose={onClose}
      destroyOnClose
      placement="right"
      zIndex={2000}
      extra={
        <Space>
          <Button
            type="text"
            aria-label={fullWidth ? "Normal drawer width" : "Expand drawer"}
            title={fullWidth ? "Normal width" : "Expand"}
            icon={fullWidth ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setFullWidth((v) => !v)}
          />
        </Space>
      }
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          status: "ACTIVE",
        }}
      >
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="Location name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="status" label="Status">
              <Select
                options={BUSINESS_LOCATION_STATUSES.map((v) => ({
                  value: v,
                  label: formatEnumLabel(v),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Short description" />
        </Form.Item>

        <Divider style={{ margin: "12px 0" }} />
        <Typography.Text strong>Files</Typography.Text>
        <Form.Item name="profilePhoto" hidden>
          <Input />
        </Form.Item>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}>
            <Form.Item label="Profile Photo">
              <Upload
                fileList={profilePhotoFiles}
                maxCount={1}
                accept="image/*"
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                  removeIcon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                }}
                customRequest={(opts) => uploadToField(opts, "profilePhoto", "PHOTO")}
                onChange={({ fileList }) => setProfilePhotoFiles(fileList)}
                onRemove={() => {
                  form.setFieldValue("profilePhoto", null);
                  setProfilePhotoFiles([]);
                  return true;
                }}
              >
                <Button loading={profilePhotoUploading}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="businessLocationType"
              label="Location Type"
              rules={[{ required: true, message: "Location type is required" }]}
            >
              <Select
                placeholder="Select location type"
                loading={locationTypesLoading}
                allowClear
                showSearch
                optionFilterProp="label"
                options={locationTypes.map((t) => ({
                  value: t.id,
                  label: `${t.label} (${t.code})`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <h4 style={{ marginTop: 16 }}>Contact Information</h4>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name={["contactInformation", "primaryEmail"]}
              label="Primary Email"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="email@domain.com" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["contactInformation", "primaryPhone"]} label="Primary Phone">
              <Input placeholder="+1..." />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name={["contactInformation", "secondaryEmail"]}
              label="Secondary Email"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="email@domain.com" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["contactInformation", "secondaryPhone"]} label="Secondary Phone">
              <Input placeholder="+1..." />
            </Form.Item>
          </Col>
        </Row>

        <h4 style={{ marginTop: 16 }}>Location Information</h4>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name={["locationInformation", "addressLine1"]} label="Address Line 1">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["locationInformation", "addressLine2"]} label="Address Line 2">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={["locationInformation", "city"]} label="City">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "state"]} label="State">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "zipCode"]} label="Zip Code">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={["locationInformation", "county"]} label="County">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "country"]} label="Country">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={["locationInformation", "latitude"]} label="Latitude">
              <InputNumber style={{ width: "100%" }} placeholder="e.g. 47.6062" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "longitude"]} label="Longitude">
              <InputNumber style={{ width: "100%" }} placeholder="e.g. -122.3321" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "placeId"]} label="Place ID">
              <Input placeholder="Google Place ID" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
}
